import { PROMOTION_TYPES } from '@/domainLogic/index.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import { createPageFromOffset } from '@/app/components/search/utils/pagerUtils.ts';
import {
  isTourOperatorDealFrance,
  isTourOperatorDealOther,
} from '@/app/components/search/utils/businessLogic/tourOperator.ts';
import getSearchParams from '@/app/components/search/common/getSearchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { RESULT_VIEW } from '@/app/common/tracking/trackingTypes';
import type { DealCard } from '../../../components/search/utils/buildDealCards/dealCardTypes';

export const RESULT_SHOWN = 'RESULT_SHOWN';
export const RESULT_SHOWN_VERSION = '2.2';

export type ResultShownParams = {
  searchId: string;
  offset?: number;
  dealCards?: DealCard[];
  listView: boolean | null;
  localization: Localization;
};

export type ResultShownPayload = {
  searchId: string;
  pageId: number;
  resultQty: number;
  wegResultQty: number;
  orchestraResultQty: number;
  ghResultQty: number;
  hotelBedsResultQty: number;
  bookingcomResultQty: number;
  expediaResultQty: number;
  alternativeaccommodationQty: number;
  resultView: RESULT_VIEW;
  hotelLabelQty?: string;
  resultVoucherQty: number;
  loyaltyResultQty: number;
  hotelTaxesAvailableQty: number;
  numberOfResultsWithTransferQty: number;
  totalResultsExcludingHotelResort: number;
};

export const createResultShownPayload = ({
  searchId,
  offset,
  dealCards,
  listView,
  localization,
}: ResultShownParams): ResultShownPayload => {
  const searchIdToUse = searchId || getSearchParams()[URLParameters.SEARCH_ID] || 'unknown';
  const pageId = createPageFromOffset(offset);
  const results = Array.isArray(dealCards) ? dealCards : [];
  const resultQty = results.length;
  const wegResultQty = results.filter((dealCard) => isTourOperatorDealOther(dealCard.product, localization)).length;
  const alternativeaccommodationQty = results.filter(
    (dealCard) => dealCard.product?.accommodation?.isAlternative
  ).length;
  const orchestraResultQty = results.filter((dealCard) =>
    isTourOperatorDealFrance(dealCard.product, localization)
  ).length;
  const ghResultQty = results.filter((dealCard) => dealCard.product?.accommodation?.providerId === 108).length;
  const hotelBedsResultQty = results.filter((dealCard) => dealCard.product?.accommodation?.providerId === 31).length;
  const bookingcomResultQty = results.filter((dealCard) => dealCard.product?.accommodation?.providerId === 2).length;
  const expediaResultQty = results.filter((dealCard) => dealCard.product?.accommodation?.providerId === 117).length;
  const resultView = listView ? RESULT_VIEW.LIST : RESULT_VIEW.MAP;
  const hotelLabelQty = `Family:${results
    .filter(
      (dealCard) =>
        dealCard?.product?.accommodation?.facilities?.familyBabySitting ||
        dealCard?.product?.accommodation?.facilities?.familyChildrenClubs ||
        dealCard?.product?.accommodation?.facilities?.familyChildrenMeal ||
        dealCard?.product?.accommodation?.facilities?.familyChildrenPool
    )
    .length.toString()},Couple:${results
    .filter((dealCard) => dealCard?.product?.accommodation?.facilities?.adultsOnly)
    .length.toString()}`;
  const resultVoucherQty = results.filter((dealCard) => dealCard.product?.promotions?.voucher?.code).length;
  const loyaltyResultQty = results.filter((dealCard) => dealCard.product?.rate?.flags?.loyalty).length;
  const hotelTaxesAvailableQty = results.filter((dealCard) => (dealCard.product?.rate?.price?.taxes ?? 0) > 0).length;
  const numberOfResultsWithTransferQty = results.filter((dealCard) =>
    dealCard.promotionTypes.includes(PROMOTION_TYPES.TRANSFER)
  ).length;
  const totalResultsExcludingHotelResort = results.filter(
    (dealCard) => ![1, 2].includes(dealCard.product?.accommodation?.typeId)
  ).length;

  const payload = {
    searchId: searchIdToUse,
    pageId,
    resultQty,
    wegResultQty,
    orchestraResultQty,
    ghResultQty,
    hotelBedsResultQty,
    bookingcomResultQty,
    expediaResultQty,
    alternativeaccommodationQty,
    resultView,
    hotelLabelQty,
    resultVoucherQty,
    loyaltyResultQty,
    hotelTaxesAvailableQty,
    numberOfResultsWithTransferQty,
    totalResultsExcludingHotelResort,
  };
  return payload;
};
